import type { ReactNode } from "react";
import styled from "styled-components";

import { Label } from "../../atoms/Label";
import { Typography } from "../../atoms/Typography";
import { Snackbar } from "../../atoms/Snackbar";
import { Button } from "../../atoms/Button";
import { PageBody } from "../PageBody";
import { defaultTheme } from "../../themes";
import okIcon from "../../../assets/icons/ok.png";
import errorIcon from "../../../assets/icons/alert.png";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";

/**
 * ウェルカムメール再送ページ(V2)の共通レイアウト
 *
 * 契約情報の表示・再送ボタン・スナックバーを提供し、
 * 再送方法ごとに異なる注意書きと入力欄は children で受け取る。
 */
export type ResendWelcomeMailV2Target = {
  id: string;
  contractId: string;
  name: string;
  address: string;
  managerMailAddress: string;
  managerName: string;
  pdt: {
    id: string;
    name: string;
    iconPath: string;
  };
  members: {
    id: string;
    name: string;
  }[];
};

export type SnackBar = {
  type: "ok" | "error";
  message: string;
  isOpen: boolean;
  close: () => void;
} | null;

export type ResendWelcomeMailLayoutProps = {
  resendWelcomeMailTarget: ResendWelcomeMailV2Target;
  onResendWelcomeMailButtonClick: () => void;
  resendWelcomeMailProcessing: boolean;
  snackBar: SnackBar;
  onCancelButtonClick: () => void;
  children: ReactNode;
};

const ContentWrapper = styled.div<{
  $padding?: string;
}>`
  padding: ${(props) => props.$padding || "20px 40px"};
  display: flex;
  flex-wrap: wrap;
`;

export const ItemWrapper = styled.div<{
  width?: string;
  $marginBottom?: string;
  $caution?: boolean;
  $bold?: boolean;
}>`
  ${(props) => props.$bold && `font-weight: bold`};
  ${(props) => props.$caution && `color: ${props.theme.colors.caution}`};
  ${(props) => props.$marginBottom && `margin-bottom: ${props.$marginBottom}`};
  ${(props) => props.width && `width: ${props.width}`};
  padding: 0 30px 5px 0;
`;

export const CautionText = styled.p<{ $marginBottom?: string }>`
  color: ${defaultTheme.colors.caution};
  ${(props) => props.$marginBottom && `margin-bottom: ${props.$marginBottom}`};
  line-height: 1.5;
`;

const ButtonWrapper = styled.div<{ $marginTop?: string; width?: string }>`
  ${(props) => props.$marginTop && `margin-top: ${props.$marginTop}`};
  width: ${(props) => props.width || "214px"};
  height: 44px;
`;

const PdtName = styled.p`
  align-items: center;
  display: flex;
  margin-bottom: 40px;
`;

const PdtIcon = styled.img`
  border-radius: 5px;
  border: 1px solid #d6dde8;
  margin-right: 10px;
`;

export function ResendWelcomeMailLayout({
  resendWelcomeMailTarget,
  onResendWelcomeMailButtonClick,
  resendWelcomeMailProcessing,
  snackBar = null,
  onCancelButtonClick,
  children,
}: ResendWelcomeMailLayoutProps) {
  return (
    <>
      <PageBody
        title={"ウェルカムメール再送"}
        buttonTitle="中止"
        buttonType="danger"
        buttonOutline
        buttonIcon={
          <FontAwesomeIcon icon={faTimes} color={defaultTheme.colors.caution} />
        }
        onButtonClick={() => {
          onCancelButtonClick();
        }}
      >
        <>
          <ContentWrapper>
            <PdtName>
              <PdtIcon
                src={resendWelcomeMailTarget.pdt.iconPath}
                alt={resendWelcomeMailTarget.pdt.name}
                width="40"
                height="40"
              />{" "}
              {resendWelcomeMailTarget.pdt.name}
            </PdtName>
            <ItemWrapper width="100%" $marginBottom="10px">
              <Label text={"医療機関ID"} margin="0 0 7px 0" />
              <p>{resendWelcomeMailTarget.id}</p>
            </ItemWrapper>
            <ItemWrapper>
              <Label text={"医療機関名"} margin="0 0 7px 0" />
              <p>{resendWelcomeMailTarget.name}</p>
            </ItemWrapper>
            <ItemWrapper>
              <Label text={"住所"} margin="0 0 7px 0" />
              <p>{resendWelcomeMailTarget.address}</p>
            </ItemWrapper>
            <ItemWrapper>
              <Label text={"製品管理責任者"} margin="0 0 7px 0" />
              <p>{resendWelcomeMailTarget.managerName}</p>
            </ItemWrapper>
          </ContentWrapper>
          <ContentWrapper $padding="20px 40px 100px 40px">
            <ItemWrapper width="100%" $marginBottom="12px">
              <Label text={"連絡先メールアドレス"} margin="0 0 7px 0" />
              <p>{resendWelcomeMailTarget.managerMailAddress}</p>
            </ItemWrapper>
            {children}
            <ButtonWrapper $marginTop="12px">
              <Button
                onClick={() => onResendWelcomeMailButtonClick()}
                disabled={resendWelcomeMailProcessing}
                buttonType={"primary"}
              >
                <Typography size={1}>ウェルカムメールを再送する</Typography>
              </Button>
            </ButtonWrapper>
          </ContentWrapper>
        </>
      </PageBody>
      {!!snackBar && (
        <Snackbar
          isOpen={snackBar.isOpen}
          icon={
            <img
              src={snackBar.type === "ok" ? okIcon : errorIcon}
              width="18"
              height="18"
              alt={snackBar.type}
            />
          }
          text={snackBar.message}
          close={snackBar.close}
        />
      )}
    </>
  );
}
